/**
 * Questionnaire Context - Global state management for questionnaire
 * Provides shared state and actions across all questionnaire components
 */

import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { FormData, QuestionnaireState, Section } from '../types/questionnaire';

interface QuestionnaireContextType {
  state: QuestionnaireState;
  formData: FormData;
  sections: Section[];
  isLoading: boolean;
  error: string | null;
  // Actions
  setSections: (sections: Section[]) => void;
  updateAnswer: (questionId: string, answer: any) => void;
  updateFormData: (data: Partial<FormData>) => void;
  setCurrentQuestion: (sectionIndex: number, questionIndex: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  resetQuestionnaire: () => void;
  markSectionComplete: (sectionId: string) => void;
  calculateProgress: () => number;
}

const initialState: QuestionnaireState = {
  currentSection: 0,
  currentQuestion: 0,
  responses: new Map(),
  completedSections: new Set(),
  isComplete: false,
  progress: 0
};

type QuestionnaireAction =
  | { type: 'SET_SECTIONS'; payload: Section[] }
  | { type: 'UPDATE_ANSWER'; payload: { questionId: string; answer: any; sectionId?: string } }
  | { type: 'UPDATE_FORM_DATA'; payload: Partial<FormData> }
  | { type: 'SET_CURRENT_QUESTION'; payload: { sectionIndex: number; questionIndex: number } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET' }
  | { type: 'MARK_SECTION_COMPLETE'; payload: string }
  | { type: 'SET_COMPLETE'; payload: boolean }
  | { type: 'UPDATE_PROGRESS'; payload: number };

function questionnaireReducer(
  state: QuestionnaireState,
  action: QuestionnaireAction
): QuestionnaireState {
  switch (action.type) {
    case 'SET_SECTIONS':
      return state; // Sections are stored separately
    
    case 'UPDATE_ANSWER':
      const newResponses = new Map(state.responses);
      newResponses.set(action.payload.questionId, {
        questionId: action.payload.questionId,
        answer: action.payload.answer,
        timestamp: new Date(),
        sectionId: action.payload.sectionId || ''
      });
      return {
        ...state,
        responses: newResponses
      };
    
    case 'UPDATE_FORM_DATA':
      return state; // FormData is stored separately
    
    case 'SET_CURRENT_QUESTION':
      return {
        ...state,
        currentSection: action.payload.sectionIndex,
        currentQuestion: action.payload.questionIndex
      };
    
    case 'SET_LOADING':
      return state; // Loading is stored separately
    
    case 'SET_ERROR':
      return state; // Error is stored separately
    
    case 'RESET':
      return initialState;
    
    case 'MARK_SECTION_COMPLETE':
      const newCompletedSections = new Set(state.completedSections);
      newCompletedSections.add(action.payload);
      return {
        ...state,
        completedSections: newCompletedSections
      };
    
    case 'SET_COMPLETE':
      return {
        ...state,
        isComplete: action.payload
      };
    
    case 'UPDATE_PROGRESS':
      return {
        ...state,
        progress: action.payload
      };
    
    default:
      return state;
  }
}

const QuestionnaireContext = createContext<QuestionnaireContextType | undefined>(undefined);

interface QuestionnaireProviderProps {
  children: ReactNode;
  initialFormData?: FormData;
  initialSections?: Section[];
}

export const QuestionnaireProvider: React.FC<QuestionnaireProviderProps> = ({
  children,
  initialFormData = {},
  initialSections = []
}) => {
  const [state, dispatch] = useReducer(questionnaireReducer, initialState);
  const [formData, setFormData] = React.useState<FormData>(initialFormData);
  const [sections, setSectionsState] = React.useState<Section[]>(initialSections);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const setSections = useCallback((newSections: Section[]) => {
    setSectionsState(newSections);
  }, []);

  const updateAnswer = useCallback((questionId: string, answer: any) => {
    const currentSection = sections[state.currentSection];
    const sectionId = currentSection?.id || '';
    
    dispatch({
      type: 'UPDATE_ANSWER',
      payload: { questionId, answer, sectionId }
    });

    // Update form data
    setFormData(prev => ({
      ...prev,
      [questionId]: answer
    }));
  }, [sections, state.currentSection]);

  const updateFormData = useCallback((data: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  }, []);

  const setCurrentQuestion = useCallback((sectionIndex: number, questionIndex: number) => {
    dispatch({
      type: 'SET_CURRENT_QUESTION',
      payload: { sectionIndex, questionIndex }
    });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const setErrorState = useCallback((err: string | null) => {
    setError(err);
  }, []);

  const resetQuestionnaire = useCallback(() => {
    dispatch({ type: 'RESET' });
    setFormData({});
    setSectionsState([]);
    setIsLoading(false);
    setError(null);
  }, []);

  const markSectionComplete = useCallback((sectionId: string) => {
    dispatch({ type: 'MARK_SECTION_COMPLETE', payload: sectionId });
  }, []);

  const calculateProgress = useCallback((): number => {
    if (sections.length === 0) return 0;
    
    // Calculate total visible questions across all sections
    let totalQuestions = 0;
    sections.forEach(section => {
      // This would need to use the actual visibility logic
      // For now, we'll use the section's questions length
      totalQuestions += section.questions?.length || 0;
    });
    
    const answeredQuestions = state.responses.size;
    return totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
  }, [sections, state.responses]);

  // Update progress automatically
  React.useEffect(() => {
    const progress = calculateProgress();
    dispatch({ type: 'UPDATE_PROGRESS', payload: progress });
  }, [calculateProgress, state.responses]);

  const value: QuestionnaireContextType = {
    state,
    formData,
    sections,
    isLoading,
    error,
    setSections,
    updateAnswer,
    updateFormData,
    setCurrentQuestion,
    setLoading,
    setError: setErrorState,
    resetQuestionnaire,
    markSectionComplete,
    calculateProgress
  };

  return (
    <QuestionnaireContext.Provider value={value}>
      {children}
    </QuestionnaireContext.Provider>
  );
};

export const useQuestionnaire = (): QuestionnaireContextType => {
  const context = useContext(QuestionnaireContext);
  if (context === undefined) {
    throw new Error('useQuestionnaire must be used within a QuestionnaireProvider');
  }
  return context;
};


